<template>
  <button
    v-if="cue !== 'absent'"
    type="button"
    dir="rtl"
    class="cue"
    :aria-expanded="expanded ? 'true' : 'false'"
    :aria-controls="panelId"
    @click="$emit('activate')"
  >
    <span class="cue-dot" :class="dotClass"></span>
    <span class="cue-label" :class="labelClass" aria-live="polite">{{ label }}</span>
  </button>
</template>

<script>
// Readiness cue (UI_SPEC C2 / UX §7.3). Sits in the `manual_text` label row,
// immediately after the existing AI button. The row is LTR, so appending puts
// the cue to the RIGHT of that button (UI reviewer issue 5).
//
// The cue is the ONLY element allowed to change appearance while the recruiter
// types, and it never changes the row height (22px < the ~27px AI button).
// `aria-live` sits on the LABEL span, not on the button, so pending->ready is
// announced once without stealing focus.
const LABELS = {
  preparing: 'מכינים…',
  ready: 'הצעה מוכנה',
  applied: 'הוזן',
  unavailable: 'אין הצעה'
}

export default {
  name: 'CvReadinessCue',
  props: {
    cue: {
      type: String,
      default: 'absent'
    },
    panelId: {
      type: String,
      required: true
    },
    expanded: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    label () {
      return LABELS[this.cue] || ''
    },
    dotClass () {
      return `is-${this.cue}`
    },
    labelClass () {
      return this.cue === 'ready' || this.cue === 'applied' ? 'is-ok' : 'is-muted'
    }
  }
}
</script>

<style scoped>
.cue {
  display: inline-block;
  height: 22px;
  line-height: 20px;
  padding: 2px 6px;
  margin-left: 8px;
  background: transparent;
  border: 1px solid transparent; /* reserves the geometry so hover cannot shift the row */
  border-radius: 4px;
  font-family: inherit;
  cursor: pointer;
  vertical-align: middle;
}
.cue:hover {
  background: #f1f3f5;
}
.cue:focus-visible {
  outline: 2px solid #3273dc;
  outline-offset: 1px;
}
.cue-label {
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}
.cue-label.is-ok {
  /* #28a745 fails AA as text — the green is a dot fill only. */
  color: #1e7e34;
}
.cue-label.is-muted {
  color: #6c757d;
}
.cue-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-left: 6px;
  vertical-align: middle;
}
.cue-dot.is-preparing {
  background: #adb5bd;
  animation: cue-pulse 1.6s ease-in-out infinite;
}
.cue-dot.is-ready {
  background: #28a745;
}
.cue-dot.is-applied {
  background: transparent;
  border: 2px solid #28a745;
}
.cue-dot.is-unavailable {
  background: transparent;
  border: 1px solid #adb5bd;
}
@keyframes cue-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
@media (prefers-reduced-motion: reduce) {
  .cue-dot.is-preparing {
    animation: none;
    opacity: 0.6;
  }
}
</style>
